// src/components/principal/ParentInvite.jsx

import React, { useEffect, useState } from "react";
import api from "../../services/api";

const ParentInvite = ({ onError, loading: loadingProp }) => {
  const [inviteCode, setInviteCode] = useState("");
  const [inviteUrl, setInviteUrl] = useState("");
  const [inviteMeta, setInviteMeta] = useState(null);
  const [loadingInvite, setLoadingInvite] = useState(false);

  const [parentAccounts, setParentAccounts] = useState([]);
  const [parentsLoading, setParentsLoading] = useState(false);

  const generate = async () => {
    setLoadingInvite(true);
    try {
      const { data } = await api.post("/auth/generate-parent-invite", {});

      // Backend now returns: invite_code, signup_url, expires_at, expires_in_days
      setInviteCode(data.invite_code || "");
      setInviteUrl(
        data.signup_url ||
          (data.invite_code
            ? `https://ghrianghraffy.com/parent-signup?code=${data.invite_code}`
            : "")
      );
      setInviteMeta({
        expires_at: data.expires_at || null,
        expires_in_days:
          typeof data.expires_in_days === "number" ? data.expires_in_days : null,
      });
    } catch (e) {
      console.error(e);
      onError?.("Failed to generate invite code. Please try again.");
    } finally {
      setLoadingInvite(false);
    }
  };

  const copyLink = () => {
    const link =
      inviteUrl ||
      (inviteCode
        ? `https://ghrianghraffy.com/parent-signup?code=${inviteCode}`
        : "");
    if (!link) {
      onError?.("There is no invite link to copy yet.");
      return;
    }
    navigator.clipboard
      .writeText(link)
      .catch(() => onError?.("Failed to copy invite link."));
  };

  const fetchParents = async () => {
    setParentsLoading(true);
    try {
      // 🔗 matches backend: @router.get("/parent-accounts", ...)
      const { data } = await api.get("/auth/parent-accounts");
      setParentAccounts(data || []);
    } catch (e) {
      console.error(e);
      onError?.("Failed to load registered parent accounts.");
    } finally {
      setParentsLoading(false);
    }
  };

  useEffect(() => {
    fetchParents();
  }, []);

  const effectiveInviteUrl =
    inviteUrl ||
    (inviteCode
      ? `https://ghrianghraffy.com/parent-signup?code=${inviteCode}`
      : "");

  return (
    <div className="parents-content">
      {/* Invite code section */}
      <h3>Parent Invite Code</h3>
      <button
        onClick={generate}
        disabled={loadingInvite || loadingProp}
      >
        Generate Invite Code for Parents
      </button>

      {inviteCode && (
        <div className="invite-code-display">
          <p>
            Send parents this link:{" "}
            <a
              href={effectiveInviteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="template-download-link"
            >
              {effectiveInviteUrl}
            </a>
            <button onClick={copyLink} className="copy-button">
              Copy Link
            </button>
          </p>

          {inviteMeta?.expires_in_days !== null && (
            <p className="invite-expiry-info">
              This invite link expires in{" "}
              <strong>
                {inviteMeta.expires_in_days === 0
                  ? "less than 1 day"
                  : `${inviteMeta.expires_in_days} day${
                    inviteMeta.expires_in_days === 1 ? "" : "s"
                  }`}
              </strong>
              .
            </p>
          )}

          {/* 💡 Helper text for principals */}
          <div className="parent-invite-helper">
            <h4>How to use this with parents</h4>
            <ul>
              <li>
                Paste this link into your{" "}
                <strong>school newsletter, email, or text message</strong> to
                parents.
              </li>
              <li>
                Parents click the link and are brought to the{" "}
                <strong>Parent Sign-Up</strong> page for your school.
              </li>
              <li>
                They create their own <strong>Parent account</strong> using
                their email and a password.
              </li>
              <li>
                Once registered, they can log in and{" "}
                <strong>view approved photos</strong> from your school.
              </li>
            </ul>
          </div>
        </div>
      )}

      <hr />

      {/* Registered parent accounts section */}
      <h3>Registered Parent Accounts</h3>

      {parentsLoading ? (
        <p>Loading parent accounts…</p>
      ) : parentAccounts.length === 0 ? (
        <p>No parent accounts have been created yet.</p>
      ) : (
        <div className="parent-accounts-wrapper">
          <ul className="parent-accounts-list">
            {parentAccounts.map((p, idx) => (
              <li key={p.email || idx}>
                <strong>{p.email}</strong>
                {p.display_name ? ` — ${p.display_name}` : ""}
                {p.created_at_iso && (
                  <span className="parent-created-at">
                    {" "}
                    (joined{" "}
                    {new Date(p.created_at_iso).toLocaleDateString()}
                    )
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <button
        type="button"
        onClick={fetchParents}
        disabled={parentsLoading}
        className="refresh-parents-button"
      >
        Refresh List
      </button>
    </div>
  );
};

export default ParentInvite;




